import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useGetInterviewSettings } from "@src/appV2/InterviewSettings/api/useGetInterviewSettings";
import { isHomeHealthOrHospice } from "@src/appV2/redesign/utils/facility";
import {
  NotificationPreferencesDialog,
  type NotificationPreferencesDialogProps,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/NotificationPreferencesDialog";
import {
  type NotificationChangeArguments,
  type NotificationRow,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/types";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Dispatch, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RateNegotiationConfigEnum } from "src/components/RateNegotiationModal/interfaces";
import { NOTIFICATION_DETAILS } from "@src/appV2/FacilityUser/notifications/notificationDetails";
import { BatchModal } from "src/containers/myAccount/BatchModal";
import { buildNotificationCellState } from "src/containers/teamMembers/buildNotificationCellState";
import { type TeamMemberActions } from "src/containers/teamMembers/teamMemberReducer";
import { useRateNegotiationFlag } from "src/hooks/useRateNegotiationFlag";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";
import { SessionType } from "src/modules/interface";
import { type IUser } from "src/modules/myAccount/types";
import { configureAlertSettings, setFlags, updateNotification } from "src/modules/myAccount/utils";

import { HomeHealthNotificationPreferencesDialog } from "./HomeHealthNotificationPreferencesDialog";

interface ContainerProps {
  member?: IUser;
  visibility: boolean;
  dispatch: Dispatch<TeamMemberActions>;
  onMemberSaved?: () => void;
}

function hasMobilePhone(member: IUser): boolean {
  return isDefined(member.phone) && member.phone.trim().length > 0;
}

export function NotificationPreferencesDialogContainer(props: ContainerProps) {
  const { member, visibility, dispatch: parentDispatch, onMemberSaved } = props;

  const reduxDispatch = useDispatch();
  const featureFlags = useFlags();
  const flags = useSelector((state: SessionType) => state.myAccount.flags);
  const alertSettings = useSelector((state: SessionType) => state.myAccount.alertSettings);
  const profile = useSelector((state: SessionType) => state.session.profile);
  const sessionUser = useSelector((state: SessionType) => state.session.user);
  const { wrapRestrictedAction } = useRestrictedSignedProfile();

  const { data: interviewSettings, isLoading: isLoadingInterviewSettings } =
    useGetInterviewSettings(
      { workplaceId: profile?.userId },
      { enabled: isDefined(profile) && visibility }
    );

  useEffect(() => {
    if (flags) {
      return;
    }
    reduxDispatch(setFlags(featureFlags));
  }, [reduxDispatch, featureFlags, flags]);

  useEffect(() => {
    if (!flags) {
      return;
    }
    configureAlertSettings(reduxDispatch, member);
  }, [reduxDispatch, flags, member]);

  const rateNegotiationDBStatus = profile?.rateNegotiation;
  const { featureFlagStatus: rateNegotiationFFStatus } = useRateNegotiationFlag({
    isAdmin: false,
    facilityRateNegotiationFlag: rateNegotiationDBStatus,
    facilityId: profile?.userId,
    facilityMsa: profile?.fullAddress?.metropolitanStatisticalArea ?? "",
    facilityState: profile?.fullAddress?.state ?? "",
  });

  const handleClose = useCallback(() => {
    parentDispatch({
      type: "SET_NOTIFICATIONS_MODAL_VISIBILITY",
      notificationModal: false,
    });
    parentDispatch({ type: "SET_MODAL_MEMBER", member: undefined });
  }, [parentDispatch]);

  const handleDone = useCallback(() => {
    parentDispatch({
      type: "SET_NOTIFICATIONS_MODAL_VISIBILITY",
      notificationModal: false,
    });
  }, [parentDispatch]);

  const handleChangeNotification = useCallback(
    (args: NotificationChangeArguments) => {
      void updateNotification(
        reduxDispatch,
        args.value,
        args.alertType.toUpperCase(),
        String(args.notificationKey),
        member
      );
    },
    [reduxDispatch, member]
  );

  const handleSelectorOpen = useMemo(
    () =>
      wrapRestrictedAction(() => undefined, {
        triggeredBy: "edit notifications",
        triggeredFrom: "Team Members",
      }),
    [wrapRestrictedAction]
  );

  const rows = useMemo<NotificationRow[]>(() => {
    if (!alertSettings || !member) {
      return [];
    }

    const allEmailDisabled = alertSettings.EMAIL.All?.default?.enabled === false;
    const allSmsDisabled = alertSettings.SMS.All?.default?.enabled === false;
    const memberHasNoPhone = !hasMobilePhone(member);
    const memberPushSettings = member.alertSettings?.PUSH ?? {};

    const enabled = Object.values(NOTIFICATION_DETAILS)
      .map((preference) => ({
        ...preference,
        description: preference.description.replace(" me ", " user "),
      }))
      .filter(({ flag }) => {
        if (!flag) {
          return true;
        }
        if (flag === CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION) {
          return (
            rateNegotiationFFStatus && rateNegotiationDBStatus === RateNegotiationConfigEnum.ON
          );
        }
        // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'FacilityFlags'.
        return flags?.[flag];
      })
      .filter(({ email }) => {
        if (
          email === "candidateApplyEmail" &&
          (isLoadingInterviewSettings || interviewSettings?.scheduleInterviews === true)
        ) {
          return false;
        }
        return true;
      });

    return enabled.map<NotificationRow>((notification, index) => {
      const emailFlagName = notification.emailFlag;
      const smsFlagName = notification.smsFlag;

      return {
        rowKey: `${String(notification.email)}-${String(notification.sms)}-${index}`,
        description: notification.description,
        cells: {
          email: buildNotificationCellState({
            alertType: "email",
            alertSetting: alertSettings.EMAIL[String(notification.email)]?.default,
            notification,
            notificationKey: notification.email,
            allDisabled: allEmailDisabled && String(notification.email) !== "All",
            // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'FacilityFlags'.
            flagDisabled: isDefined(emailFlagName) && !flags?.[emailFlagName],
            fieldDisabled: false,
          }),
          sms: buildNotificationCellState({
            alertType: "sms",
            alertSetting: alertSettings.SMS[String(notification.sms)]?.default,
            notification,
            notificationKey: notification.sms,
            allDisabled: memberHasNoPhone || (allSmsDisabled && String(notification.sms) !== "All"),
            // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'FacilityFlags'.
            flagDisabled: isDefined(smsFlagName) && !flags?.[smsFlagName],
            fieldDisabled: false,
          }),
          push: buildNotificationCellState({
            alertType: "push",
            alertSetting:
              typeof notification.push === "string"
                ? memberPushSettings[notification.push]
                : undefined,
            notification,
            notificationKey: typeof notification.push === "string" ? notification.push : false,
            allDisabled: false,
            flagDisabled: false,
            fieldDisabled: typeof notification.push !== "string",
          }),
        },
      };
    });
  }, [
    alertSettings,
    flags,
    interviewSettings?.scheduleInterviews,
    isLoadingInterviewSettings,
    member,
    rateNegotiationDBStatus,
    rateNegotiationFFStatus,
  ]);

  if (!member) {
    return null;
  }

  if (isHomeHealthOrHospice(profile?.type)) {
    return (
      <HomeHealthNotificationPreferencesDialog
        isOpen={visibility}
        performedBy={sessionUser?._id}
        user={member}
        workplaceId={profile?.userId ?? ""}
        onClose={handleClose}
        onDone={handleDone}
        onSaved={onMemberSaved}
      />
    );
  }

  const dialogProps: NotificationPreferencesDialogProps = {
    isOpen: visibility,
    rows,
    smsDisabledNoPhone: !hasMobilePhone(member),
    onChangeNotification: handleChangeNotification,
    onSelectorOpen: handleSelectorOpen,
    onClose: handleClose,
    onDone: handleDone,
  };

  return (
    <>
      <NotificationPreferencesDialog {...dialogProps} />
      <BatchModal />
    </>
  );
}
